import { useMutation, useQueryClient, useQuery } from '@tanstack/react-query';
import { apiClient } from '../api/axios';
import { login, LoginResponse } from '../api/login';
import { create } from 'zustand';
import { toast } from 'react-toastify';
import { useNavigate } from 'react-router-dom';
import { useProfileContext } from './useProfileContext';
import { ProfileResponse } from '../api/profile';
interface AuthStore {
  isAuthenticated: boolean;
  setIsAuthenticated: (value: boolean) => void;
}

export const useAuthStore = create<AuthStore>((set) => ({
  isAuthenticated: localStorage.getItem('isAuthenticated') === 'true',
  setIsAuthenticated: (value) => set({ isAuthenticated: value }),
}));

interface LoginCredentials {
  email: string;
  password: string;
}

export const useAuth = () => {
  return useQuery({
    queryKey: ['auth'],
    queryFn: () => {
      const isAuthenticated =
        localStorage.getItem('isAuthenticated') === 'true';
      return Promise.resolve(isAuthenticated);
    },
  });
};

export const useLogin = () => {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const setIsAuthenticated = useAuthStore((state) => state.setIsAuthenticated);
  const { setProfile } = useProfileContext();

  return useMutation<LoginResponse, Error, LoginCredentials>({
    mutationFn: (credentials) => {
      return login(credentials.email, credentials.password);
    },
    onSuccess: (response) => {
      localStorage.setItem('auth_token', response.tokens.token);
      localStorage.setItem('refresh_token', response.tokens.refreshToken);
      localStorage.setItem('isAuthenticated', 'true');
      setIsAuthenticated(true);
      queryClient.setQueryData(['auth'], true);
      queryClient.setQueryData(['profile'], response.user);
      setProfile(response.user as unknown as ProfileResponse);
      navigate('/admin');
      return response;
    },
    onError: (error) => {
      toast.error(error.message);
    },
  });
};

export const useLogout = () => {
  const queryClient = useQueryClient();
  const setIsAuthenticated = useAuthStore((state) => state.setIsAuthenticated);
  const navigate = useNavigate();
  return useMutation({
    mutationFn: () => {
      return apiClient.post('auth/logout');
    },
    onSuccess: () => {
      localStorage.removeItem('auth_token');
      localStorage.removeItem('refresh_token');
      localStorage.removeItem('isAuthenticated');
      setIsAuthenticated(false);
      queryClient.setQueryData(['auth'], false);
      queryClient.clear();
      navigate('/login');
    },
  });
};

// Hook to access auth state
export const useIsAuthenticated = () => {
  return useAuthStore((state) => state.isAuthenticated);
};
